import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Collapse,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { aboutUsApi } from '../api/aboutUs';
import { AboutUs, UpdateAboutUsDto } from '../api/aboutUs';

interface FormErrors {
  description?: {
    en?: string;
    ru?: string;
  };
  techStack?: {
    backend?: {
      title?: {
        en?: string;
        ru?: string;
      };
      technologies?: string[];
    };
    frontend?: {
      title?: {
        en?: string;
        ru?: string;
      };
      technologies?: string[];
    };
    mobile?: {
      title?: {
        en?: string;
        ru?: string;
      };
      technologies?: string[];
    };
  };
}

export default function AboutUsPage() {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<UpdateAboutUsDto>({});
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [expandedSections, setExpandedSections] = useState({
    description: true,
    techStack: true,
    additionalExpertiseInfo: true,
  });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch main about us
  const { data: aboutUs, isLoading, error, refetch } = useQuery<AboutUs>({
    queryKey: ['aboutUs'],
    queryFn: aboutUsApi.getMain,
    retry: 3,
    retryDelay: 1000,
  });

  useEffect(() => {
    if (aboutUs) {
      setFormData({
        name: aboutUs.name || '',
        description: aboutUs.description || { en: '', ru: '' },
        techStack: aboutUs.techStack || {
          backend: { title: { en: '', ru: '' }, technologies: [] },
          frontend: { title: { en: '', ru: '' }, technologies: [] },
          mobile: { title: { en: '', ru: '' }, technologies: [] },
        },
        additionalExpertiseInfo: aboutUs.additionalExpertiseInfo || { en: '', ru: '' },
        isActive: aboutUs.isActive,
      } as UpdateAboutUsDto);
      setFormErrors({});
    }
  }, [aboutUs]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: aboutUsApi.updateMain,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aboutUs'] });
      setSnackbar({
        open: true,
        message: 'About us information updated successfully!',
        severity: 'success'
      });
      setFormErrors({});
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error updating about us information: ${error instanceof Error ? error.message : 'Unknown error'}`,
        severity: 'error'
      });
    },
  });

  // Initialize mutation
  const initializeMutation = useMutation({
    mutationFn: aboutUsApi.initialize,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aboutUs'] });
      setSnackbar({
        open: true,
        message: 'About us information initialized successfully!',
        severity: 'success'
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error initializing about us information: ${error instanceof Error ? error.message : 'Unknown error'}`,
        severity: 'error'
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const errors: FormErrors = {};

    if (!formData.description?.en?.trim()) {
      errors.description = { ...errors.description, en: 'English description is required' };
    }
    if (!formData.description?.ru?.trim()) {
      errors.description = { ...errors.description, ru: 'Russian description is required' };
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setSnackbar({
        open: true,
        message: 'Please fix the validation errors',
        severity: 'error'
      });
      return;
    }

    updateMutation.mutate(formData);
  };

  const handleTechStackChange = (section: 'backend' | 'frontend' | 'mobile', field: 'title' | 'technologies', value: any) => {
    setFormData(prev => ({
      ...prev,
      techStack: {
        ...prev.techStack,
        [section]: {
          ...prev.techStack?.[section],
          [field]: value,
        },
      },
    } as UpdateAboutUsDto));
  };

  const handleTechnologiesChange = (section: 'backend' | 'frontend' | 'mobile', value: string) => {
    const technologies = value.split(',').map(tech => tech.trim()).filter(tech => tech);
    handleTechStackChange(section, 'technologies', technologies);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <InfoIcon color="primary" />
          <Typography variant="h4" component="h1">
            About Us Management
          </Typography>
        </Box>

        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error</AlertTitle>
          {error instanceof Error ? error.message : 'Failed to load about us information'}
        </Alert>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => refetch()}
            startIcon={<RefreshIcon />}
          >
            Retry
          </Button>
          <Button
            variant="contained"
            onClick={() => initializeMutation.mutate()}
            disabled={initializeMutation.isPending}
            startIcon={initializeMutation.isPending ? <CircularProgress size={20} /> : <RefreshIcon />}
          >
            {initializeMutation.isPending ? 'Initializing...' : 'Initialize Default'}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <InfoIcon color="primary" />
        <Typography variant="h4" component="h1">
          About Us Management
        </Typography>
      </Box>

      {updateMutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {updateMutation.error?.message || 'An error occurred while saving the about us information'}
        </Alert>
      )}

             <form onSubmit={handleSubmit}>
         <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
           {/* Description Section */}
           <Box>
             <Card>
               <CardContent>
                 <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                   <Typography variant="h6" sx={{ flexGrow: 1 }}>
                     Company Description
                   </Typography>
                   <IconButton
                     onClick={() => setExpandedSections(prev => ({ ...prev, description: !prev.description }))}
                   >
                     {expandedSections.description ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                   </IconButton>
                 </Box>

                 <Collapse in={expandedSections.description}>
                   <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                     <Box>
                       <TextField
                         fullWidth
                         multiline
                         rows={6}
                         label="Description (English)"
                         value={formData.description?.en || ''}
                         onChange={(e) => setFormData(prev => ({
                           ...prev,
                           description: { ...prev.description, en: e.target.value }
                         } as UpdateAboutUsDto))}
                         error={!!formErrors.description?.en}
                         helperText={formErrors.description?.en}
                       />
                     </Box>
                     <Box>
                       <TextField
                         fullWidth
                         multiline
                         rows={6}
                         label="Description (Russian)"
                         value={formData.description?.ru || ''}
                         onChange={(e) => setFormData(prev => ({
                           ...prev,
                           description: { ...prev.description, ru: e.target.value }
                         } as UpdateAboutUsDto))}
                         error={!!formErrors.description?.ru}
                         helperText={formErrors.description?.ru}
                       />
                     </Box>
                   </Box>
                 </Collapse>
               </CardContent>
             </Card>
           </Box>

           {/* Tech Stack Section */}
           <Box>
             <Card>
               <CardContent>
                 <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                   <Typography variant="h6" sx={{ flexGrow: 1 }}>
                     Technology Stack
                   </Typography>
                   <IconButton
                     onClick={() => setExpandedSections(prev => ({ ...prev, techStack: !prev.techStack }))}
                   >
                     {expandedSections.techStack ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                   </IconButton>
                 </Box>

                 <Collapse in={expandedSections.techStack}>
                   <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
                     {/* Backend */}
                     <Box>
                       <Typography variant="h6" gutterBottom>Backend</Typography>
                       <TextField
                         fullWidth
                         label="Title (English)"
                         value={formData.techStack?.backend?.title?.en || ''}
                         onChange={(e) => handleTechStackChange('backend', 'title', {
                           ...formData.techStack?.backend?.title,
                           en: e.target.value
                         })}
                         sx={{ mb: 2 }}
                       />
                       <TextField
                         fullWidth
                         label="Title (Russian)"
                         value={formData.techStack?.backend?.title?.ru || ''}
                         onChange={(e) => handleTechStackChange('backend', 'title', {
                           ...formData.techStack?.backend?.title,
                           ru: e.target.value
                         })}
                         sx={{ mb: 2 }}
                       />
                       <TextField
                         fullWidth
                         label="Technologies (comma-separated)"
                         value={formData.techStack?.backend?.technologies?.join(', ') || ''}
                         onChange={(e) => handleTechnologiesChange('backend', e.target.value)}
                         helperText="Enter technologies separated by commas"
                       />
                     </Box>

                     {/* Frontend */}
                     <Box>
                       <Typography variant="h6" gutterBottom>Frontend</Typography>
                       <TextField
                         fullWidth
                         label="Title (English)"
                         value={formData.techStack?.frontend?.title?.en || ''}
                         onChange={(e) => handleTechStackChange('frontend', 'title', {
                           ...formData.techStack?.frontend?.title,
                           en: e.target.value
                         })}
                         sx={{ mb: 2 }}
                       />
                       <TextField
                         fullWidth
                         label="Title (Russian)"
                         value={formData.techStack?.frontend?.title?.ru || ''}
                         onChange={(e) => handleTechStackChange('frontend', 'title', {
                           ...formData.techStack?.frontend?.title,
                           ru: e.target.value
                         })}
                         sx={{ mb: 2 }}
                       />
                       <TextField
                         fullWidth
                         label="Technologies (comma-separated)"
                         value={formData.techStack?.frontend?.technologies?.join(', ') || ''}
                         onChange={(e) => handleTechnologiesChange('frontend', e.target.value)}
                         helperText="Enter technologies separated by commas"
                       />
                     </Box>

                     {/* Mobile */}
                     <Box>
                       <Typography variant="h6" gutterBottom>Mobile</Typography>
                       <TextField
                         fullWidth
                         label="Title (English)"
                         value={formData.techStack?.mobile?.title?.en || ''}
                         onChange={(e) => handleTechStackChange('mobile', 'title', {
                           ...formData.techStack?.mobile?.title,
                           en: e.target.value
                         })}
                         sx={{ mb: 2 }}
                       />
                       <TextField
                         fullWidth
                         label="Title (Russian)"
                         value={formData.techStack?.mobile?.title?.ru || ''}
                         onChange={(e) => handleTechStackChange('mobile', 'title', {
                           ...formData.techStack?.mobile?.title,
                           ru: e.target.value
                         })}
                         sx={{ mb: 2 }}
                       />
                       <TextField
                         fullWidth
                         label="Technologies (comma-separated)"
                         value={formData.techStack?.mobile?.technologies?.join(', ') || ''}
                         onChange={(e) => handleTechnologiesChange('mobile', e.target.value)}
                         helperText="Enter technologies separated by commas"
                       />
                     </Box>
                   </Box>
                 </Collapse>
               </CardContent>
             </Card>
           </Box>

           {/* Additional Expertise Info Section */}
           <Box>
             <Card>
               <CardContent>
                 <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                   <Typography variant="h6" sx={{ flexGrow: 1 }}>
                     Additional Expertise Information
                   </Typography>
                   <IconButton
                     onClick={() => setExpandedSections(prev => ({ ...prev, additionalExpertiseInfo: !prev.additionalExpertiseInfo }))}
                   >
                     {expandedSections.additionalExpertiseInfo ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                   </IconButton>
                 </Box>

                 <Collapse in={expandedSections.additionalExpertiseInfo}>
                   <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                     <Box>
                       <TextField
                         fullWidth
                         multiline
                         rows={8}
                         label="Additional Expertise Info (English)"
                         value={formData.additionalExpertiseInfo?.en || ''}
                         onChange={(e) => setFormData(prev => ({
                           ...prev,
                           additionalExpertiseInfo: { ...prev.additionalExpertiseInfo, en: e.target.value }
                         } as UpdateAboutUsDto))}
                         helperText="Enter detailed information about your expertise in high-load systems, computer graphics, complex mobile applications, EV charging services, etc."
                       />
                     </Box>
                     <Box>
                       <TextField
                         fullWidth
                         multiline
                         rows={8}
                         label="Additional Expertise Info (Russian)"
                         value={formData.additionalExpertiseInfo?.ru || ''}
                         onChange={(e) => setFormData(prev => ({
                           ...prev,
                           additionalExpertiseInfo: { ...prev.additionalExpertiseInfo, ru: e.target.value }
                         } as UpdateAboutUsDto))}
                         helperText="Введите подробную информацию о вашей экспертизе в высоконагруженных системах, компьютерной графике, сложных мобильных приложениях, сервисах зарядки электромобилей и т.д."
                       />
                     </Box>
                   </Box>
                 </Collapse>
               </CardContent>
             </Card>
           </Box>

           {/* Status */}
           <Box>
             <Card>
               <CardContent>
                 <FormControlLabel
                   control={
                     <Switch
                       checked={formData.isActive ?? true}
                       onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                     />
                   }
                   label="Active"
                 />
               </CardContent>
             </Card>
           </Box>
         </Box>

        {/* Action Buttons */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => initializeMutation.mutate()}
            disabled={initializeMutation.isPending}
            startIcon={initializeMutation.isPending ? <CircularProgress size={20} /> : <RefreshIcon />}
          >
            {initializeMutation.isPending ? 'Initializing...' : 'Initialize Default'}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={updateMutation.isPending}
            startIcon={updateMutation.isPending ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}